'use client'

import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import Image from 'next/image'
import Link from 'next/link'
import useSWR from 'swr'

/**
 * Component to display top livery designs sold in the last week.
 *
 * @param {Object} liveryDesign - The design of the livery.
 * @param {Array} games - All games globally.
 * @param {number} countSoldLastWeek - The count of how many times the livery was sold in the last week, added in useEffect in parent.
 * @param {number} index - The index of the livery in the display list.
 * @returns {JSX.Element} The rendered component.
 */
export default function LiveryOverviewTopLiveriesWeek({
  liveryDesign,
  games,
  countSoldLastWeek,
  index,
}) {
  const { data: designFiles, isLoading: designFilesLoading } = useSWR(
    {
      url: `livery-design/${liveryDesign.id}/file`,
    },
    apiFetch,
  )

  return (
    <div
      className={cn(
        'mt-4 flex flex-col items-center desktop-sm:flex-row',
        index !== 0 &&
          'border-0 border-neutral-blue-700 pt-2 desktop-sm:border-t desktop-sm:pt-4',
      )}
    >
      <div className="flex h-auto w-full desktop-sm:h-32 desktop-sm:w-fit">
        <Link
          href={`/profile-manager/liveries/edit-livery/${liveryDesign.id}`}
          className="o relative aspect-video w-full overflow-hidden rounded pt-[56%]"
        >
          {!designFilesLoading && designFiles.length > 0 ? (
            <Image
              fill
              alt="Event Header Image"
              className="relative overflow-hidden rounded-md object-cover desktop-sm:rounded-sm"
              src={
                designFiles?.length > 0
                  ? designFiles?.find(
                      (liveryFile) =>
                        liveryFile.usageHint === 'user-livery-image',
                    )?.fileUrl
                  : '/img/default/header.webp'
              }
            />
          ) : (
            <Image
              fill
              alt="Event Header Image"
              className="relative overflow-hidden rounded-md object-cover desktop-sm:rounded-sm"
              src={'/img/default/header.webp'}
            />
          )}
        </Link>
      </div>
      <div className="relative mr-4 mt-4 flex w-[calc(100%-2rem)] flex-col desktop-sm:ml-4 desktop-sm:mt-0">
        <Link
          className="truncate-3 text-[22px] font-semibold text-white"
          href={`/profile-manager/liveries/edit-livery/${liveryDesign.id}`}
        >
          {liveryDesign.name}{' '}
          <span className="text-p-2 font-normal text-neutral-blue-500">
            #{countSoldLastWeek} sold
          </span>
        </Link>
        <div className="font-normal text-neutral-blue-500">
          <div className="truncate-1 mt-0 flex font-inter text-[16px] text-neutral-blue-500">
            {[
              ...liveryDesign.platforms?.map((ld2p) => ld2p.platform.nicename),
              games?.find((s) => s.name === liveryDesign.gameName).nicename,
            ].join(' • ')}
          </div>
          {liveryDesign?.car?.name && (
            <div className="truncate-1">{liveryDesign?.car.name}</div>
          )}
        </div>
      </div>
    </div>
  )
}
